import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import Header from '../components/Header'
import { ImagesModal } from '../components/quotation'
import {
  AssistantPreview,
  ChatInput,
  AttachedFiles,
  DragOverlay,
  ErrorMessage
} from '../components/assistant'
import { useAssistantFlow } from '../hooks/useAssistantFlow'
import { useFileUpload } from '../hooks/useFileUpload'
import OpportunityDecisionModal from '../components/OpportunityDecisionModal'
import { colors, borderRadius } from '../styles/tokens'
import { divisaoValida, mensagemDeIntencao } from './intencaoDeHotel'

/**
 * Página do Assistente de Cotação
 *
 * Refatorado seguindo Single Responsibility Principle.
 * Cada componente tem uma única responsabilidade.
 *
 * Fluxo: chat → preview → images → redirect
 */
export default function AssistantQuotation() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const API_BASE = process.env.REACT_APP_API_URL || '/api'
  const [prompt, setPrompt] = useState('')

  const {
    files,
    isDragging,
    fileInputRef,
    handleFilesChange,
    handleRemoveFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    clearFiles,
    openFilePicker
  } = useFileUpload()

  const {
    step,
    preview,
    clientName,
    contactId,
    erroContato,
    loading,
    error,
    correction,
    refining,
    profile,
    issues,
    imageUrl1,
    imageUrl2,
    existingImages,
    hotelImages,
    carImages,
    tourImages,
    setStep,
    setClientName,
    setContactId,
    setCorrection,
    setImageUrl1,
    setImageUrl2,
    handleSubmit,
    handleCorrection,
    handleConfirmPreview,
    handleImagesSubmit,
    handleImageChange,
    validateImages,
    resetFlow,
    criarCotacaoDaOferta,
    decisaoOportunidade,
    decidindoOportunidade,
    decidirOportunidade,
    cancelarOportunidade
  } = useAssistantFlow()

  const [creatingTemplate, setCreatingTemplate] = useState(null)

  const TEMPLATE_LABELS = {
    passport: 'Passaporte',
    visa_new: 'Visto — 1ª Via',
    visa_renewal_no_interview: 'Renovação s/ Entrevista',
    visa_renewal_interview: 'Renovação c/ Entrevista'
  }

  // Handler para selecionar template — sempre vem do chat com contactId
  const handleSelectTemplate = async (templateKey) => {
    setCreatingTemplate(templateKey)
    try {
      // Produto de catálogo não passa pela decisão de oportunidade — o
      // backend responde com `pode_perguntar=False`. Fluxo idêntico ao
      // anterior, sem modal.
      const result = await api.createQuoteFromTemplate(templateKey, {
        seller_id: profile?.seller_id,
        client_name: clientName || 'Cliente',
        contact_id: contactId
      })
      if (result.id) {
        navigate(`/app/cotacao/${result.id}`)
      }
    } catch (err) {
      console.error('Erro ao criar template:', err)
    } finally {
      setCreatingTemplate(null)
    }
  }

  // ── Produto rápido HOTEL ────────────────────────────────────────────
  //
  // IDA: um clique. Publica a intenção com o contact_id e abre o portal.
  // Nada é perguntado aqui — o vendedor vai preencher cidade e datas no
  // HotelDO de qualquer forma, e pedir isso duas vezes é trabalho inventado.
  //
  // A cotação NÃO nasce agora: `Quote.create` recusa destino vazio, e neste
  // instante ninguém sabe qual hotel será escolhido. Ela nasce na VOLTA.
  const HOTELDO = 'https://ag00030559-hoteldo.e-agencias.com/'
  const handleSelectHotel = () => {
    const intencao = mensagemDeIntencao(contactId)
    if (intencao) window.postMessage(intencao, window.location.origin)
    window.open(HOTELDO, '_blank', 'noopener')
  }

  // ── VOLTA do HotelDO ────────────────────────────────────────────────
  //
  // A extensão traz o vendedor de volta com os identificadores do que ele
  // escolheu. Cidade, datas e total de hóspedes vêm da própria oferta; a
  // única coisa que falta é a DIVISÃO entre adultos e crianças, que o
  // fornecedor não informa e `para_bloco` recusa adivinhar.
  const volta = {
    job: searchParams.get('hotel_job'),
    pack: searchParams.get('pack'),
    destino: searchParams.get('destino'),
    checkin: searchParams.get('checkin'),
    checkout: searchParams.get('checkout'),
    hotel: searchParams.get('hotel'),
    // null quando o fornecedor nao deu um total legivel. Ver divisaoValida.
    hospedes: (() => {
      const cru = searchParams.get('hospedes')
      const n = Number(cru)
      return cru && Number.isInteger(n) && n > 0 ? n : null
    })(),
    // A divisao ja foi decidida no popup, ANTES da ingestao: o job de detalhe
    // nasceu com ela e o `para_bloco` a confere contra o total do fornecedor.
    // Aqui ela e exibida para conferencia, nao para edicao — mudar so aqui
    // faria a cotacao discordar do bloco.
    adultos: Number(searchParams.get('adultos')) || 0,
    criancas: Number(searchParams.get('criancas')) || 0,
  }
  const ehVolta = !!(volta.job && volta.pack && volta.destino
                     && volta.checkin && volta.checkout)

  const somaFecha = divisaoValida(volta.adultos, volta.criancas, volta.hospedes)
  // O nome vem do CRM, e a busca e assincrona. Enquanto ele nao chega, a
  // cotacao nao pode nascer: se o dado obrigatorio nao existe, a operacao
  // nao acontece. Travar e explicar e melhor que gravar um nome inventado.
  const temCliente = !!(clientName && clientName.trim())
  const [concluindo, setConcluindo] = useState(false)
  const [erroVolta, setErroVolta] = useState(null)

  const handleConcluirHotel = async () => {
    setConcluindo(true)
    setErroVolta(null)
    try {
      // 1. a cotação nasce da oferta, passando pela decisão de oportunidade
      const quote = await criarCotacaoDaOferta({
        destino: volta.destino, checkin: volta.checkin, checkout: volta.checkout,
        adultos: volta.adultos, criancas: volta.criancas,
        contatoDaBusca: searchParams.get('contact_id'),
      })
      if (quote?.__cancelled) return       // vendedor desistiu — sem erro
      if (!quote?.id) {
        setErroVolta(quote?.message || quote?.error || 'não consegui criar a cotação')
        return
      }
      // 2. o bloco entra pelo P2b, reconstruído no servidor
      const res = await fetch(`${API_BASE}/hoteis/detalhes/${volta.job}/bloco`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_pack_id: volta.pack, quote_id: quote.id }),
      })
      if (!res.ok) {
        const erro = await res.json().catch(() => ({}))
        setErroVolta(`a cotação foi criada, mas o hotel não entrou: ${erro.message || res.status}`)
        return
      }
      navigate(`/app/cotacao/${quote.id}`)
    } catch (e) {
      setErroVolta(String(e?.message || e))
    } finally {
      setConcluindo(false)
    }
  }

  // Handler para produto rápido Aéreo — navega para estação de voos com contact_id
  const handleSelectAereo = () => {
    const params = new URLSearchParams()
    if (contactId) params.set('contact_id', contactId)
    if (clientName) params.set('contact_name', clientName)
    navigate(`/app/aereo?${params.toString()}`)
  }

  const onSubmit = () => { handleSubmit(prompt, files) }
  const onReset = () => { resetFlow(); setPrompt(''); clearFiles() }
  const onFilesChange = (e) => { handleFilesChange(e) }

  const quickBtnStyle = {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    padding: '0.75rem 1rem',
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: creatingTemplate ? 'wait' : 'pointer',
    transition: 'all 0.15s'
  }

  return (
    <div
      style={{ minHeight: '100vh', backgroundColor: colors.background, fontFamily: "'Inter', sans-serif" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <OpportunityDecisionModal
        decisao={decisaoOportunidade} busy={decidindoOportunidade}
        onDecide={decidirOportunidade} onCancel={cancelarOportunidade}
      />
      <Header subtitle="Assistente de Cotação" userName={profile?.name || 'Usuário'} />
      <DragOverlay visible={isDragging} />

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* VOLTA DO HOTELDO — a oferta escolhida vira cotação.
            Só uma pergunta: como dividir os hóspedes. O total vem travado
            pelo que o fornecedor cotou, e `para_bloco` confere de novo no
            servidor. Não adivinhamos composição: em hotel, uma criança muda
            preço, quarto e regra de ocupação. */}
        {ehVolta && (
          <div style={{
            marginBottom: '1.5rem', padding: '1.25rem',
            border: `2px solid ${colors.primary}`, borderRadius: borderRadius.md,
            backgroundColor: colors.white
          }}>
            <p style={{ margin: 0, fontSize: '0.7rem', letterSpacing: '.06em',
                        textTransform: 'uppercase', color: colors.text.muted }}>
              escolhido no HotelDO
            </p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '1.1rem', fontWeight: 700,
                        color: colors.primary }}>
              {volta.hotel || volta.destino}
            </p>
            <p style={{ margin: '0.15rem 0 1rem', fontSize: '0.85rem', color: colors.text.secondary }}>
              {volta.destino} · {volta.checkin.split('-').reverse().join('/')} →{' '}
              {volta.checkout.split('-').reverse().join('/')} · {volta.hospedes} hóspede(s)
            </p>

            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center',
                          flexWrap: 'wrap', marginBottom: '0.9rem' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.7rem', color: colors.text.muted }}>Hóspedes</p>
                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>
                  {volta.adultos} adulto(s){volta.criancas > 0 && ` · ${volta.criancas} criança(s)`}
                </p>
              </div>
              <button
                onClick={handleConcluirHotel}
                disabled={!somaFecha || !temCliente || concluindo}
                style={{
                  padding: '0.6rem 1.3rem', fontSize: '0.9rem', fontWeight: 600,
                  color: '#fff', border: 'none', borderRadius: borderRadius.sm,
                  backgroundColor: somaFecha && temCliente && !concluindo ? colors.primary : '#94a3b8',
                  cursor: somaFecha && temCliente && !concluindo ? 'pointer' : 'default'
                }}
              >
                {concluindo ? 'criando…' : 'Criar cotação com este hotel'}
              </button>
            </div>
            {!temCliente && (
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem',
                          color: erroContato ? '#dc2626' : '#b45309' }}>
                {erroContato
                  ? `${erroContato} A cotação só pode ser criada com o cliente identificado.`
                  : 'carregando o contato… a cotação só pode ser criada com o cliente identificado.'}
              </p>
            )}
            {!somaFecha && (
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem', color: '#b45309' }}>
                a divisão de hóspedes não fecha com os {volta.hospedes} que o
                fornecedor cotou — volte ao painel da extensão e ajuste lá, que é
                onde o job foi criado.
              </p>
            )}
            {erroVolta && (
              <p style={{ margin: '0.6rem 0 0', fontSize: '0.8rem', color: '#dc2626' }}>
                {erroVolta}
              </p>
            )}
          </div>
        )}

        <button
          onClick={() => navigate("/app")}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            marginBottom: "1rem", padding: "0.5rem 1rem",
            backgroundColor: "transparent", border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.md, color: colors.text.secondary,
            fontSize: "0.875rem", cursor: "pointer"
          }}
        >
          ← Voltar para cotações
        </button>

        <div style={{
          backgroundColor: colors.card, borderRadius: borderRadius.lg,
          border: `1px solid ${colors.border}`, overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem', borderBottom: `1px solid ${colors.border}`,
            backgroundColor: 'rgba(79, 70, 229, 0.05)'
          }}>
            <p style={{ margin: 0, color: colors.text.primary }}>
              💬 Olá! Me conta sobre a cotação que você quer criar.
            </p>
            <p style={{ margin: '0.5rem 0 0', color: colors.text.secondary, fontSize: '0.875rem' }}>
              Ex: "pacote bariloche 2 adultos com voo hotel e passeios"
            </p>
          </div>

          {step === 'preview' && preview && (
            <AssistantPreview
              preview={preview}
              clientName={clientName}
              setClientName={setClientName}
              contactId={contactId}
              setContactId={setContactId}
              correction={correction}
              setCorrection={setCorrection}
              refining={refining}
              loading={loading}
              onCorrection={handleCorrection}
              onConfirm={handleConfirmPreview}
              onReset={onReset}
              issues={issues}
            />
          )}

          <ErrorMessage error={error} />

          {step === 'chat' && (
            <AttachedFiles files={files} onRemove={handleRemoveFile} />
          )}

          {step === 'chat' && (
            <ChatInput
              prompt={prompt}
              setPrompt={setPrompt}
              files={files}
              loading={loading}
              fileInputRef={fileInputRef}
              onSubmit={onSubmit}
              onOpenFilePicker={openFilePicker}
            />
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={onFilesChange}
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>

          {/* ═══ Produtos Rápidos ═══ */}
          <div style={{
            marginBottom: '1.5rem', backgroundColor: colors.card,
            borderRadius: borderRadius.lg, border: `1px solid ${colors.border}`,
            padding: '1.25rem', textAlign: 'left'
          }}>
            <p style={{
              fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: colors.text.muted, margin: '0 0 0.75rem 0'
            }}>
              Produtos Rápidos
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>

              {/* 🏨 Hotel — produto rápido, irmão do Aéreo */}
              <button
                onClick={handleSelectHotel}
                disabled={!!creatingTemplate}
                style={quickBtnStyle}
              >
                <span style={{ fontSize: '1.25rem' }}>🏨</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>
                    Hotel
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>Buscar no HotelDO</p>
                </div>
              </button>

              {/* ✈️ Aéreo — novo produto rápido */}
              <button
                onClick={handleSelectAereo}
                disabled={!!creatingTemplate}
                style={quickBtnStyle}
              >
                <span style={{ fontSize: '1.25rem' }}>✈️</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>
                    Aéreo
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>Buscar voos</p>
                </div>
              </button>

              {/* Passaporte */}
              <button onClick={() => handleSelectTemplate('passport')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>📘</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Passaporte</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 407,25</p>
                </div>
              </button>

              {/* Visto — Primeira Via */}
              <button onClick={() => handleSelectTemplate('visa_new')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Visto — 1ª Via</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

              {/* Visto — Renovação sem entrevista */}
              <button onClick={() => handleSelectTemplate('visa_renewal_no_interview')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Renovação s/ Entrevista</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

              {/* Visto — Renovação com entrevista */}
              <button onClick={() => handleSelectTemplate('visa_renewal_interview')} disabled={!!creatingTemplate} style={quickBtnStyle}>
                <span style={{ fontSize: '1.25rem' }}>🇺🇸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary }}>Renovação c/ Entrevista</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: colors.text.muted }}>R$ 1.850</p>
                </div>
              </button>

            </div>

          </div>

          <button
            onClick={() => navigate('/app/nova')}
            style={{
              background: 'none', border: 'none', color: colors.text.secondary,
              cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline'
            }}
          >
            Usar fluxo tradicional
          </button>
        </div>
      </main>

      {step === 'images' && (
        <ImagesModal
          destination={preview?.destination}
          imageUrl1={imageUrl1}
          imageUrl2={imageUrl2}
          setImageUrl1={setImageUrl1}
          setImageUrl2={setImageUrl2}
          existingImages={existingImages}
          hotelImages={hotelImages}
          carImages={carImages}
          tourImages={tourImages}
          onImageChange={handleImageChange}
          onSubmit={handleImagesSubmit}
          onBack={() => setStep('preview')}
          loading={loading}
          isValid={validateImages()}
        />
      )}
    </div>
  )
}
